import React, { useCallback, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@apollo/react-hooks'

import { useIsKid } from '../../../hooks/use-is-kid'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'

import { checkQueryStatus } from '../../../lib/utils'
import {
  getMainNavHeaderSectionLinks,
  getPropertiesCount,
  addKidsLinkForNonKidProfiles
} from '../utils/navigation-links-helpers'
import {
  getProfileNameAndAvatarUri,
  getSelectedProfileIdFromSession
} from '../../../lib/account'
import { getModalLocation } from '../../../lib/modal'
import { MODALS } from '../../../constants'
import { setNavigationVisibility } from '../../../actions'
import { useNavigationAccount } from '../hooks/use-navigation-account'

import NavigationItem from '../navigation-item'
import NavigationBurgerIcon from '../navigation-burger-icon'
import NavigationSettings from '../navigation-settings'
import NavigationSideUi from './navigation-side-ui'
import NavigationAccountSide from './navigation-account-side'

import NAV_HEADER_QUERY from '../../../../graphql/queries/navigation-header.gql'
import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'

const NavigationSide = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const isKid = useIsKid()
  const isAuthenticated = useIsAuthenticated()
  const { myRentalsTotalItems } = useNavigationAccount()

  const [isAccountNavOpen, setIsAccountNavOpen] = useState(false)

  // Redux state
  const sideNav = useSelector(state => state.navigation.sideNav)
  const session = useSelector(state => state.session)
  const profileId = getSelectedProfileIdFromSession(session)

  // GraphQL queries
  const { data: accountData, loading: accountLoading, error: accountError } = useQuery(ACCOUNT_QUERY, {
    skip: !isAuthenticated,
    notifyOnNetworkStatusChange: true
  })

  const { data: navHeaderData, loading: navHeaderLoading, error: navHeaderError } = useQuery(NAV_HEADER_QUERY, {
    fetchPolicy: 'cache-only'
  })

  // Early return if queries are loading or have errors
  if (checkQueryStatus({ loading: accountLoading, error: accountError }) ||
      checkQueryStatus({ loading: navHeaderLoading, error: navHeaderError })) {
    return null
  }

  // Handlers
  const handleProfileChange = useCallback(() => {
    history.push(getModalLocation(location, MODALS.qsParams.profilesSwitch))
  }, [history, location])

  const onClickOutside = useCallback(() => {
    if (sideNav) {
      dispatch(setNavigationVisibility(false))
    }
  }, [sideNav, dispatch])

  const onSideNavLinkClick = useCallback(() => {
    dispatch(setNavigationVisibility(false))
  }, [dispatch])

  const onToggleAccountNav = useCallback(() => {
    setIsAccountNavOpen(!isAccountNavOpen)
  }, [isAccountNavOpen])

  // Process data
  const { name, avatarUri } = getProfileNameAndAvatarUri(accountData)
  const mainSectionChildren = getMainNavHeaderSectionLinks(navHeaderData)
  const mainSectionLinks = addKidsLinkForNonKidProfiles(isAuthenticated, isKid, mainSectionChildren)
  const propertiesCount = getPropertiesCount(mainSectionLinks)

  const props = {
    display: sideNav,
    isAuthenticated,
    profileId,
    name,
    avatarUri,
    isKid,
    mainSectionLinks,
    propertiesCount,
    isAccountNavOpen,
    handleProfileChange,
    onClickOutside,
    onSideNavLinkClick,
    onToggleAccountNav,
    myRentalsTotalItems,
    navigationItem: NavigationItem,
    navigationBurgerIcon: NavigationBurgerIcon,
    navigationSettings: NavigationSettings,
    navigationAccountSide: NavigationAccountSide
  }

  return <NavigationSideUi {...props} />
}

export default NavigationSide
